import Link from 'next/link';
import React from 'react';

interface ButtonProps {
  children?: React.ReactNode;
  loadingText?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'accent'
    | 'accent-dark'
    | 'icon'
    | 'primary-outline'
    | 'secondary-outline'
    | 'danger-outline'
    | 'accent-outline'
    | 'accent-dark-outline'
    | 'white'
    | 'white-outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  disabled?: boolean;
  onClick?:
    | ((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void)
    | ((e: React.MouseEvent<HTMLButtonElement>) => void)
    | ((e: React.MouseEvent<HTMLAnchorElement>) => void)
    | (() => void);
  className?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  href?: string;
  title?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  external?: boolean;
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
}

const Button = ({
  children,
  loadingText,
  type = 'button',
  variant = 'primary',
  isLoading = false,
  disabled = false,
  onClick,
  className = '',
  icon,
  iconPosition = 'left',
  size = 'md',
  href,
  target,
  external = false,
  title,
}: ButtonProps) => {
  const buttonClass = `btn ${variant} ${size} ${isLoading || disabled ? 'disabled' : ''} ${className}`;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="btn-loading-container">
          <div className="btn-spinner"></div>
          {loadingText || children}
        </div>
      );
    }

    if (icon) {
      return (
        <div className={`btn-content-with-icon`}>
          {icon && iconPosition === 'left' && <div className="btn-icon">{icon}</div>}
          {children && <span className="btn-text">{children}</span>}
          {icon && iconPosition === 'right' && <div className="btn-icon">{icon}</div>}
        </div>
      );
    }

    return children;
  };

  // If href is provided, render as a link
  if (href && !disabled && !isLoading) {
    if (external) {
      // External link - use regular anchor tag
      return (
        <a
          href={href}
          target={target}
          className={buttonClass}
          onClick={onClick as (e: React.MouseEvent<HTMLAnchorElement>) => void}
          role="button"
        >
          {renderContent()}
        </a>
      );
    } else {
      // Internal link - use Next.js Link
      return (
        <Link href={href} target={target}>
          <span
            className={buttonClass}
            onClick={onClick as (e: React.MouseEvent<HTMLAnchorElement>) => void}
            role="button"
          >
            {renderContent()}
          </span>
        </Link>
      );
    }
  }

  // Default button behavior
  return (
    <button
      title={title}
      type={type}
      disabled={disabled || isLoading}
      className={buttonClass}
      onClick={onClick as (e: React.MouseEvent<HTMLButtonElement>) => void}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
